import { TECH_INDICATIONS, DEVICE_BROWSER, DEVICE_MOBILE, DEVICE_TABLET, OS_MAC_OS, OS_IOS,
  OS_ANDROID, OS_WINDOWS, BROWSER_CHROME, BROWSER_SAFARI, BROWSER_EDGE, BROWSER_MOBILE_SAFARI } from "@amplio-common/constants";
import { camelToSnakeCase } from "@amplio-common/funcs/commonFunctionalities";
import { Trans } from "@lingui/macro";

// SUPPORT LOGIC
export const SUPPORTED_DEVICES = [DEVICE_MOBILE, DEVICE_TABLET, DEVICE_BROWSER];
export const SUPPORTED_OS = [OS_MAC_OS, OS_IOS, OS_WINDOWS, OS_ANDROID];
export const SUPPORTED_BROWSERS = [BROWSER_CHROME, BROWSER_SAFARI, BROWSER_EDGE, BROWSER_MOBILE_SAFARI];

export const MIN_BROWSER_SUPPORTED_VERSIONS = {
  [BROWSER_CHROME]: 87,
  [BROWSER_SAFARI]: 11,
  [BROWSER_EDGE]: 86,
  [BROWSER_MOBILE_SAFARI]: 11,
};

//TODO: check versions received from windows and fill the correct versions
export const MIN_OS_SUPPORTED_VERSIONS = {
  [OS_MAC_OS]: 10.13,
  [OS_IOS]: 14.3,
  [OS_WINDOWS]: 10,
  [OS_ANDROID]: 7,
};

export const MIN_INTERNET_SPEEDS = {
  downloadSpeed: 10000,
  uploadSpeed: 100,
};

export const CPU_JS_THERSHOLDS = {
  minJsNumOps: 10000,
  maxJsResolveTime: 50,
};

// const UPDATE_OS_LINKS = {
//   [OS_WINDOWS]: "https://support.microsoft.com/en-us/windows/update-windows-3c5ae7fc-9fb6-9af1-1984-b5e0412c556a",
//   [OS_MAC_OS]: "https://support.apple.com/en-il/HT201541",
//   [OS_IOS]: "https://support.apple.com/en-il/HT204204",
//   [OS_ANDROID]: "https://support.google.com/android/answer/7680439?hl=en",
// };

// const UPDATE_BROWSER_LINKS = {
//   [BROWSER_EDGE]: "https://www.microsoft.com/en-us/edge",
//   [BROWSER_CHROME]: "https://support.google.com/chrome/answer/95414?hl=en&co=GENIE.Platform%3DDesktop",
//   [BROWSER_MOBILE_SAFARI]: "https://support.apple.com/downloads/safari",
//   [BROWSER_SAFARI]: "https://support.apple.com/en-il/HT204416",
// };

// const DOWNLOAD_CHROME_LINK = "https://www.google.com/chrome/";
const UNSUPPORTED_OS_LINK = "https://intercom.help/Amplio_Learning/en/articles/5847152-what-if-my-operating-system-os-is-not-supported";
const UNSUPPORTED_BROWSER_LINK = "https://intercom.help/Amplio_Learning/en/articles/5849201-what-if-my-browser-is-not-supported";
const WEAK_INTERNET_CONNECTION_LINK = "https://intercom.help/Amplio_Learning/en/articles/5849704-what-if-i-have-a-weak-internet-connection";

export const initErrorList = {
  [TECH_INDICATIONS.DEVICE_SUPPORT]: { isActive: false },
  [TECH_INDICATIONS.OS_SUPPORT]: { isActive: false },
  [TECH_INDICATIONS.BROWSER_SUPPORT]: { isActive: false },
  [TECH_INDICATIONS.OS_VERSION_SUPPORT]: { isActive: false },
  [TECH_INDICATIONS.BROWSER_VERSION_SUPPORT]: { isActive: false },
  [TECH_INDICATIONS.INTERNET_CONNECTION]: { isActive: false },
  [TECH_INDICATIONS.CPU]: { isActive: false },
  [TECH_INDICATIONS.PERMISSIONS]: { isActive: false },
  [TECH_INDICATIONS.USER_FOCUS]: { isActive: false },
  [TECH_INDICATIONS.OPENTOK_STREAM]: { isActive: false },
};

export const getUserInitErrorList = (studentTechStatus) => {
  const errorObj = { ...initErrorList };

  const { browserName, browserVersion, deviceType, downloadSpeed, osVersion, osName, isWeakStreamConnection, jsResolveTime, jsNumOps } = studentTechStatus;

  const versionRegex = /^\d+\.?\d+$|^\d+\.?\d+(?=\.)/g; //extract the version up to the second "."
  const formattedOsVersion = Number(osVersion?.match(versionRegex)?.[0]); //todo: add regex to take number until second dot if there is one

  // is device supported
  if (deviceType && !SUPPORTED_DEVICES.includes(deviceType)) {
    errorObj[TECH_INDICATIONS.DEVICE_SUPPORT] = { isActive: true };
  }
  // is os / os version supported
  if (osName && !SUPPORTED_OS.includes(osName)) {
    errorObj[TECH_INDICATIONS.OS_SUPPORT] = { isActive: true };
  }
  //TODO: Check if Windows version is a number
  if (osName && SUPPORTED_OS.includes(osName) && MIN_OS_SUPPORTED_VERSIONS?.[osName] > formattedOsVersion) {
    errorObj[TECH_INDICATIONS.OS_VERSION_SUPPORT] = { isActive: true };
  }
  // is browser / browser version supported
  if (browserName && !SUPPORTED_BROWSERS.includes(browserName)) {
    errorObj[TECH_INDICATIONS.BROWSER_SUPPORT] = { isActive: true };
  }
  
  if (browserName && browserVersion && SUPPORTED_BROWSERS.includes(browserName) && MIN_BROWSER_SUPPORTED_VERSIONS?.[browserName] > browserVersion) {
    errorObj[TECH_INDICATIONS.BROWSER_VERSION_SUPPORT] = { isActive: true };
  }
  // is internet speed slow - currently without considering the upload speed
  if ((downloadSpeed && downloadSpeed < MIN_INTERNET_SPEEDS.downloadSpeed) || isWeakStreamConnection) {
    errorObj[TECH_INDICATIONS.INTERNET_CONNECTION] = { isActive: true };
  }
  // is cpu low
  if (((jsResolveTime && jsResolveTime > CPU_JS_THERSHOLDS.maxJsResolveTime) || (jsNumOps && jsNumOps < CPU_JS_THERSHOLDS.minJsNumOps))) {
    errorObj[TECH_INDICATIONS.CPU] = { isActive: true };
  }
  return errorObj;
};

// const getPermissionErrorText = (isVideoPermissionGranted, isAudioPermissionGranted) => {
//   const requiredPermissions = [];
//   isVideoPermissionGranted === false && requiredPermissions.push("camera");
//   isAudioPermissionGranted === false && requiredPermissions.push("microphone");

//   return requiredPermissions;
// };

export const getErrorTxt = (error, studentTechStatus) => {
  const { name: studentName /* isVideoPermissionGranted, isAudioPermissionGranted, osName, browserName */ } = studentTechStatus || {};
  // const requiredPermissions = getPermissionErrorText(isVideoPermissionGranted, isAudioPermissionGranted);
  
  const errorTextDict = {
    [TECH_INDICATIONS.DEVICE_SUPPORT]: { dialogTitleRegular: `${studentName} has an `,
      dialogTitleBold: "unsupported device",
      dialogTxtBold: "Unsupported Device.",
      dialogTxtRegular: "How to help? Change the device to a computer, laptop or tablet." },

    [TECH_INDICATIONS.OS_SUPPORT]: { dialogTitleRegular: `${studentName} has an `,
      dialogTitleBold: "unsupported operating system",
      dialogTxtBold: "Unsupported Operating System",
      dialogTxtRegular: "How to help? Use only devices with supported operating systems: Windows, macOS, Android and iOS.",
      cancelDestination: UNSUPPORTED_OS_LINK,
    },

    [TECH_INDICATIONS.OS_VERSION_SUPPORT]: { dialogTitleRegular: `${studentName} needs to `,
      dialogTitleBold: "update the operating system",
      dialogTxtBold: "Outdated operating system.",
      dialogTxtRegular: `How to help? Update the operating system. Here’s a link you can send ${studentName} over the chat!
      A caregiver’s help might be needed.`,
      // cancelDestination: UPDATE_OS_LINKS?.[osName]
      cancelDestination: UNSUPPORTED_OS_LINK,
    },

    [TECH_INDICATIONS.BROWSER_SUPPORT]: { dialogTitleRegular: `${studentName} has an `,
      dialogTitleBold: "unsupported browser.",
      dialogTxtBold: "Unsupported browser.",
      dialogTxtRegular: `How to help? 
      Use only supported browsers: Chrome, Safari or Edge. You can open the links to the browsers’ website and send them to ${studentName} over the chat!`,
      cancelDestination: UNSUPPORTED_BROWSER_LINK },

    [TECH_INDICATIONS.BROWSER_VERSION_SUPPORT]: { dialogTitleRegular: `${studentName} needs to `,
      dialogTitleBold: "update the browser version",
      dialogTxtBold: "Outdated browser Version",
      dialogTxtRegular: `How to help? Update the browser version. It can be easily done via this link. You can send it to ${studentName} over the chat!`,
      // cancelDestination: UPDATE_BROWSER_LINKS?.[browserName]
      cancelDestination: UNSUPPORTED_BROWSER_LINK,
    
    },

    [TECH_INDICATIONS.INTERNET_CONNECTION]: { dialogTitleRegular: `${studentName} has a `,
      dialogTitleBold: <Trans>weak internet connection</Trans>,
      dialogTxtBold: <Trans>Weak internet connection</Trans>,
      dialogTxtRegular: <Trans>You need to close other tabs</Trans>,
      cancelDestination: WEAK_INTERNET_CONNECTION_LINK,
    },

    [TECH_INDICATIONS.CPU]: { dialogTitleRegular: `${studentName} has a `,
      dialogTitleBold: <Trans>weak internet connection</Trans>,
      dialogTxtBold: <Trans>Weak internet connection</Trans>,
      dialogTxtRegular: <Trans>You need to close other tabs</Trans> },

    [TECH_INDICATIONS.OPENTOK_STREAM]: { dialogTitleRegular: `${studentName} has a `,
      dialogTitleBold: <Trans>weak internet connection</Trans>,
      dialogTxtBold: <Trans>Weak internet connection</Trans>,
      dialogTxtRegular: <Trans>You need to close other tabs</Trans>,
      cancelDestination: WEAK_INTERNET_CONNECTION_LINK,
    },

    [TECH_INDICATIONS.PERMISSIONS]: { dialogTitleRegular: `${studentName} has `,
      dialogTitleBold: "missing permissions",
      dialogTxtBold: "Missing permissions",
      dialogTxtRegular: `Amplio records audio from time to time during the session. Ask ${studentName} to approve the browser audio permissions.
      In case you're working remotely, ${studentName} will need to approve video permissions as well.
      Use the chat to guide!` },
    [TECH_INDICATIONS.USER_FOCUS]: {
      dialogTitleRegular: <Trans>{studentName} is not on poppins</Trans>,
      dialogTitleBold: `${studentName} צופה ב`,
      dialogTxtBold: <Trans>Ask {studentName} to open the poppins again</Trans>,
      dialogTxtRegular: "" },
    // [TECH_INDICATIONS.PERMISSIONS]: { dialogTitleRegular: `${studentName} needs to allow ${requiredPermissions.join(" & ")} permissions`,
    //   dialogTxt: "Please make sure your browser has access to both the camera and the microphone, and that they are not in use by another app",
    //   cancelAction: true },
  };

  return errorTextDict?.[error];
};

export const normalizeTechStats = (data) => {
  const snakeCasedData = Object.entries(data).reduce((res, [k, v]) => {
    res = { ...res, [camelToSnakeCase(k)]: v };
    return res;
  }, {});

  return { ...snakeCasedData };
};

